import { ParseResult } from "papaparse";
import React, { ChangeEvent, Component } from "react";
import "./TakeOffUploadModal.scss";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import IOnUploadTakeOffCsvDTO from "../../../../models/DTO/IOnUploadTakeOffCsvDTO";
import IPerformCatalogCSVInventoryUpdateDTO from "../../../../models/DTO/IPerformCatalogCSVInventoryUpdateDTO";
import IUploadTakeOffCsvDTO from "../../../../models/DTO/IUploadTakeOffCsvDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import ICatalogCSVInventoryDataRow from "../../../../models/ICatalogCSVInventoryDataRow";
import ICategory from "../../../../models/ICategory";
import IOrder from "../../../../models/IOrder";
import ISupplierModel from "../../../../models/ISupplierModel";
import ITakeOffUploadCsvDataRow from "../../../../models/ITakeOffUploadCsvDataRow";
import IUserUI from "../../../../models/IUserUI";
import OwButton from "../../../shared/OwButton/OwButton";
import OwCheckbox from "../../../shared/OwCheckbox/OwCheckbox";
import OwCsvReader from "../../../shared/OwCsvReader/OwCsvReader";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwProgressBar from "../../../shared/OwProgressBar/OwProgressBar";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import { Socket } from "socket.io-client";

type Props = {
    closeModalFn: () => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    takeOffOrder: IOrder;
    takeOffItemCategory: ICategory[];
};

type State = {
    uploadActive: boolean;
    csvData?: ParseResult<any>;
    showResults: boolean;
    recordsUpdated: number;
    recordsCreated: number;
    recordsNotUpdated: number;
    recordsErrored: number;
    totalRecords: number;
    isFormValid: boolean;
    isProcessing: boolean;
    file?: any;
    fileTypeRejected?: any;
    fileDropRejected?: any;
    autoCreateNotFoundSKU: boolean;
};

class TakeOffUploadModal extends Component<Props, State> {
    constructor(props: any) {
        super(props);

        this.state = {
            showResults: false,
            totalRecords: 0,
            recordsUpdated: 0,
            recordsCreated: 0,
            recordsNotUpdated: 0,
            recordsErrored: 0,
            uploadActive: false,
            isFormValid: false,
            isProcessing: false,
            autoCreateNotFoundSKU: true,
        };
    }

    componentDidMount(): void {
        this.props.socket.on(EndpointsEnum.Notify, this.notify);
        this.props.socket.on(EndpointsEnum.TakeOffOnUploadTakeOffCsv, this.onUploadTakeOffCsv);
    }

    componentWillUnmount(): void {
        this.props.socket.removeListener(EndpointsEnum.Notify, this.notify);
        this.props.socket.removeListener(EndpointsEnum.TakeOffOnUploadTakeOffCsv, this.onUploadTakeOffCsv);
    }

    notify = (data: IErrorDTO) => {
        this.setState({
            uploadActive: false,
        });
    };

    closeModal = () => {
        this.props.closeModalFn();
    };

    isFormValid = () => {
        let isFormValid = false;

        if (this.state.csvData) {
            isFormValid = true;
        }

        this.setState({
            isFormValid,
        });
    };

    csvLoaded = (csvData?: ParseResult<any>) => {
        if (csvData) {
            this.setState(
                {
                    csvData,
                },
                () => {
                    this.isFormValid();
                }
            );
        }
    };

    setupCSVData = (csvData: ParseResult<any>) => {
        let skuCol = "";
        let qtyCol = "";
        let widthCol = "";
        let heightCol = "";
        let depthCol = "";

        const firstRow: object = csvData.data[0];

        for (const col of Object.keys(firstRow)) {
            if (col.toLowerCase().indexOf("sku") >= 0) {
                skuCol = col;
            }

            if (col.toLowerCase().indexOf("qty") >= 0) {
                qtyCol = col;
            }

            if (col.toLowerCase().indexOf("width") >= 0) {
                widthCol = col;
            }

            if (col.toLowerCase() === "height") {
                heightCol = col;
            }

            if (col.toLowerCase() === "depth") {
                depthCol = col;
            }
        }

        const categoryCols: any = {};

        for (const col of Object.keys(firstRow)) {
            for (const categoryCol of this.props.takeOffItemCategory) {
                if (col.toLowerCase() === categoryCol.name.toLowerCase()) {
                    categoryCols[categoryCol.name] = col;
                }
            }
        }

        const items: ITakeOffUploadCsvDataRow[] = [];

        for (const row of csvData.data) {
            const dataRow: ITakeOffUploadCsvDataRow = {
                sku: row[skuCol],
            };

            if (this.props.supplierModel.has_dimensions) {
                dataRow.depth = row[depthCol];
                dataRow.height = row[heightCol];
                dataRow.width = row[widthCol];
            }

            for (const categoryCol of Object.keys(categoryCols)) {
                const category: any = categoryCols[categoryCol];

                dataRow[`${category}`] = Number(row[category]);
            }

            items.push(dataRow);
        }

        const payload: IUploadTakeOffCsvDTO = {
            userGuid: this.props.userModel.guid,
            userToken: this.props.userModel.token,
            supplierGuid: this.props.supplierModel.guid,
            rows: JSON.stringify(items),
            takeOffOrderGuid: this.props.takeOffOrder.guid,
            autoCreateNotFoundSKU: this.state.autoCreateNotFoundSKU,
        };

        this.props.socket.emit(EndpointsEnum.TakeOffUploadTakeOffCsv, payload);

        this.resetResults();
    };

    onUploadTakeOffCsv = (data: IOnUploadTakeOffCsvDTO) => {
        this.setState({
            showResults: true,
            totalRecords: data.totalRecords,
            recordsCreated: data.recordsCreated,
            recordsUpdated: data.recordsUpdated,
            recordsErrored: data.recordsErrored,
            recordsNotUpdated: data.recordsNotUpdated,
        });
    };

    doUpload = () => {
        this.setState(
            {
                uploadActive: true,
            },
            () => {
                if (this.state.csvData) {
                    this.setupCSVData(this.state.csvData);
                }
            }
        );
    };

    resetResults = () => {
        this.setState({
            showResults: false,
            recordsUpdated: 0,
            totalRecords: 0,
            recordsCreated: 0,
            recordsNotUpdated: 0,
        });
    };

    create = () => {};

    handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        const state: any = {
            [name]: value,
        };

        this.setState(state);
    };

    render() {
        return (
            <div className={"TakeOffUploadModal p-2"}>
                <div className={"w-100"} style={{ height: "2rem" }}>
                    <div className={"d-flex justify-content-between"}>
                        <OwTitle leftIconName={"fad fa-cloud-upload-alt"} title={"upload_update"} tag={"h3"} />
                        <div>
                            <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={this.closeModal} />
                        </div>
                    </div>
                </div>

                <div>
                    {this.state.showResults ? null : (
                        <div className={"mt-4"}>
                            <div className={"drop-area"}>
                                <OwCsvReader onFileLoaded={this.csvLoaded} isProcessing={this.state.isProcessing} />
                            </div>

                            <div>
                                {this.state.file ? (
                                    <div>
                                        <OwLabel title={"loaded_file"} noBottomMargin={true} isBold={true} isUnderline={true} />:{" "}
                                        {this.state.file.name}
                                    </div>
                                ) : null}
                                <div className={"d-flex justify-content-between error-section"}>
                                    <OwLabel
                                        className={"text-danger mt-2 " + (this.state.fileDropRejected ? "" : "d-none")}
                                        title={"file_drop_rejected_too_big"}
                                    />
                                    <OwLabel
                                        className={"text-danger mt-2 " + (this.state.fileTypeRejected ? "" : "d-none")}
                                        title={"file_type_invalid"}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {this.state.showResults ? null : (
                        <div className={"mt-4 d-flex flex-column"}>
                            <div className={"mb-3"}>
                                <OwCheckbox
                                    label={"auto_create_not_found_sku"}
                                    name={"autoCreateNotFoundSKU"}
                                    truthyVal={true}
                                    falsyVal={false}
                                    fn={this.handleChange}
                                    checked={this.state.autoCreateNotFoundSKU}
                                />
                            </div>
                            <div>
                                <OwButton
                                    title={"upload"}
                                    fn={this.doUpload}
                                    active={this.state.uploadActive}
                                    disabled={!this.state.isFormValid || this.state.uploadActive}
                                    disableOnActive={true}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {this.state.showResults ? (
                    <div className={"mt-2"}>
                        <OwLabel title={"results"} noBottomMargin={true} />
                        <div>
                            <OwLabel title={"total_records"} noBottomMargin={true} className={"me-3"} isBold={true} />
                            {this.state.totalRecords}
                        </div>

                        <div>
                            <OwLabel title={"records_updated"} noBottomMargin={true} className={"me-3"} isBold={true} />
                            {this.state.recordsUpdated}
                        </div>

                        <div>
                            <OwLabel title={"records_created"} noBottomMargin={true} className={"me-3"} isBold={true} />
                            {this.state.recordsCreated}
                        </div>

                        <div>
                            <OwLabel title={"records_not_updated"} noBottomMargin={true} className={"me-3"} isBold={true} />
                            {this.state.recordsNotUpdated}
                        </div>

                        <div>
                            <OwLabel title={"records_errored"} noBottomMargin={true} className={"me-3"} isBold={true} />
                            {this.state.recordsErrored}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }
}

export default TakeOffUploadModal;
